import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { logCustomEvent } from '~/api/analytics';
import { createRequestTypes, initialLoading, SIGN_IN_ACTION } from '~/actions';
import { apiMethodsConst } from '~/services/methods';
import { InvalidCredentialsError, TurnstileVerificationError } from '../errors';
import { AuthService } from '../services/AuthService';
import type { ISignInRequest } from '../types/ISignInRequest';
import type { ISignInResponse } from '../types/ISignInResponse';

const GENERIC_ERROR_MESSAGE = 'Something went wrong, please try again.';

const signInEvents = createRequestTypes(SIGN_IN_ACTION) as {
  REQUEST: string;
  SUCCESS: string;
  FAILURE: string;
};

export const useSignIn = () => {
  const dispatch = useDispatch();

  return useMutation<ISignInResponse, unknown, ISignInRequest>({
    mutationFn: (payload) => AuthService.signIn(payload),
    onSuccess: (data, variables) => {
      dispatch({ type: apiMethodsConst.SIGN_IN_REQUEST, payload: data });
      dispatch(initialLoading());
      logCustomEvent('sign_in', { email: variables.email });
    },
    onError: (error) => {
      // Wrong email/password is shown inline by the login form (banner + field
      // highlights), so we skip the global error pop-up. `mutateAsync` still
      // rejects, letting the form's submit handler catch it.
      if (error instanceof InvalidCredentialsError) {
        return;
      }

      if (error instanceof TurnstileVerificationError) {
        dispatch({
          type: signInEvents.FAILURE,
          payload: {
            title: "We couldn't verify your browser",
            message:
              "Something didn't load correctly on your end. Please refresh the page and try signing in again.",
            buttonText: 'Got it',
          },
        });
        return;
      }

      const message = error instanceof Error ? error.message : GENERIC_ERROR_MESSAGE;
      dispatch({
        type: signInEvents.FAILURE,
        payload: { message },
      });
    },
  });
};
